import { map } from 'lodash/fp';
import { connect } from 'react-redux';
import { createStructuredSelector } from 'reselect';
import { pipe } from 'fp-ts/function';
import { ReactNode, useRef, useState, FC } from 'react';
import { IconName, Stack } from '@optimus-web/core';
import { UnicodeEntity } from '@mews/enums';
import { FeatureFlag, TextKey } from '@mews-commander/enums';
import { TooltippedIcon, LocalizedDateFromUtc } from '@mews-commander/ui';
import { AppClockServiceProps, LocalizedText, useFeatureFlag, withAppClockService } from '@mews/framework';
import {
    mapCustomerClassifications,
    notEmpty,
    useCommanderSelector,
    useGainsightCommanderEvents,
} from '@mews-commander/utils';
import { getCurrentEnterpriseReservationCustomerId } from '@mews-commander/legacy-entities/src/selectors/reservations';
import { getCustomer } from '@mews-commander/legacy-entities/src/ducks/customers';
import {
    getReservationsCustomerIds,
    updateOwner as updateOwnerAction,
} from '@mews-commander/legacy-entities/src/ducks/reservations';
import { hasCustomerValidExternalCreditCard } from '@mews-commander/legacy-entities/src/ducks/paymentCards';
import { deleteCompanionship, unassignCompanionship } from '@mews-commander/legacy-entities/src/ducks/companionships';
import { getCustomerRelatedNotesString } from '@mews-commander/legacy-entities/src/ducks/accountNotes';
import { useCurrentEnterpriseId } from '@mews-commander/core';
import { Customer, useInvalidateRepositoryQuery } from '@mews-commander/data-access';
import { ApplicationState, CommanderThunkDispatch, WithScope } from '@mews-commander/types';
import { withActiveWindowLoader } from '../../../core';
import { openBillingAccount, openPayments, refetchGroupAndShowSuccessFlash } from '../actions';
import { editInternal as editInternalsAction } from '../actions/profile';
import ClassificationIcon from '../../../components/commander-ui/ClassificationIcon';
import { CustomerLink } from '../../../components/commander-ui';
import { useRoutingRuleChangesConfirmation } from '../../routingRulesNew/components/RoutingRuleChangeConfirmationPanel/useRoutingRuleChangesConfirmation';
import { useCustomerHasBirthdayDuringReservation } from './useCustomerHasBirthdayDuringReservation';

type CompanionAction = 'Unassign' | 'Delete';

interface OwnProps extends WithScope {
    customerId: string;
    reservationId: string;
    companionshipId: string;
    shouldDisplayGroupAction: boolean;
    refetchGroup(): void;
}

interface StateProps {
    customer?: Customer;
    accountNotes: string | null;
    hasValidExternalCreditCard: boolean;
    reservationCustomerId?: string | null;
}

interface DispatchProps {
    editInternals: () => void;
    openBillingScreen: () => void;
    openPaymentsScreen: () => void;
    updateOwner: () => Promise<void>;
    unassignCompanion: () => Promise<void>;
    deleteCompanion: () => Promise<void>;
}

type CompanionListItemProps = StateProps & OwnProps & DispatchProps;

const CompanionListItem: FC<CompanionListItemProps> = ({
    customer,
    accountNotes,
    hasValidExternalCreditCard,
    customerId,
    reservationCustomerId,
    openBillingScreen,
    shouldDisplayGroupAction,
    editInternals,
    updateOwner,
    unassignCompanion,
    deleteCompanion,
    openPaymentsScreen,
    reservationId,
    scope,
}: CompanionListItemProps) => {
    const hasBirthDay = useCustomerHasBirthdayDuringReservation(customerId, reservationId);
    const [routingRuleChangesDialog, setRoutingRuleChangesDialog] = useState<ReactNode>(null);
    const granularRoutingRulesConditionsExtensionEnabled = useFeatureFlag(
        FeatureFlag.granularRoutingRulesConditionsExtensionEnabled
    );
    const enterpriseId = useCurrentEnterpriseId();
    const currentAccountIds = useCommanderSelector((state) =>
        getReservationsCustomerIds(state, { ids: [reservationId], enterpriseId })
    );
    const gainsightEvents = useGainsightCommanderEvents();
    const companionActionRef = useRef<CompanionAction | null>(null);
    const invalidateQuery = useInvalidateRepositoryQuery();

    const { validate } = useRoutingRuleChangesConfirmation({
        reservationIds: [reservationId],
        accountIds: currentAccountIds.filter((id: string) => id !== customerId),
        enabled: granularRoutingRulesConditionsExtensionEnabled,
        scope,
        onSubmit: async ({ changes }) => {
            if (companionActionRef.current === 'Unassign') {
                await unassignCompanion();
            } else if (companionActionRef.current === 'Delete') {
                await deleteCompanion();
            }

            void invalidateQuery('routingRules', 'getDetailsOnReservationUpdate');

            gainsightEvents.onApiSuccess({
                context: 'BillingAutomationChanged',
                data: {
                    changes,
                    origin: 'Account',
                },
            });

            setRoutingRuleChangesDialog(null);
            companionActionRef.current = null;
        },
        onCancel: () => {
            setRoutingRuleChangesDialog(null);
            companionActionRef.current = null;
        },
    });

    if (!customer) {
        return null;
    }
    const { classifications: customerClassification } = customer;
    const classifications = mapCustomerClassifications(customerClassification);

    const shouldDisplayMakeOwner = shouldDisplayGroupAction && customerId !== reservationCustomerId;

    const handleCompanionAction = async (action: CompanionAction, operation: () => Promise<void>) => {
        const result = validate();

        if (result) {
            companionActionRef.current = action;
            setRoutingRuleChangesDialog(result.dialog);
            return;
        }

        await operation();
    };

    const handleUnassignCompanion = async () => handleCompanionAction('Unassign', unassignCompanion);
    const handleDeleteCompanion = async () => handleCompanionAction('Delete', deleteCompanion);

    return (
        <>
            <Stack itemAlignment="center" spacing="100" wrap inline>
                <span>
                    <TooltippedIcon
                        value={IconName.PaymentReport}
                        tooltip={TextKey.Billing}
                        onClick={openBillingScreen}
                    />
                    {hasValidExternalCreditCard ? (
                        <TooltippedIcon
                            value={IconName.PaymentCard}
                            tooltip={TextKey.PaymentCard}
                            onClick={openPaymentsScreen}
                        />
                    ) : null}
                    {shouldDisplayGroupAction ? (
                        <>
                            <TooltippedIcon
                                value={IconName.ChevronLeft}
                                tooltip={TextKey.Move}
                                onClick={handleUnassignCompanion}
                            />
                            <TooltippedIcon
                                value={IconName.CrossClose}
                                tooltip={TextKey.Remove}
                                onClick={handleDeleteCompanion}
                            />
                        </>
                    ) : null}
                    {shouldDisplayMakeOwner ? (
                        <TooltippedIcon
                            value={IconName.Share}
                            tooltip={TextKey.ReservationMakeOwner}
                            onClick={updateOwner}
                        />
                    ) : null}
                </span>
                <CustomerLink id={customerId} />
                {notEmpty(classifications)
                    ? map(
                          (classification) => (
                              <ClassificationIcon
                                  onClick={editInternals}
                                  key={classification.value}
                                  classification={classification}
                              />
                          ),
                          classifications
                      )
                    : null}
                {accountNotes ? (
                    <TooltippedIcon value={IconName.Notes} tooltip={accountNotes} onClick={editInternals} />
                ) : null}
                {hasBirthDay ? (
                    <TooltippedIcon
                        value={IconName.Birthday}
                        tooltip={
                            <>
                                <LocalizedText textKey={TextKey.DateOfBirth} />
                                {UnicodeEntity.Space}
                                {customer.birthDate ? <LocalizedDateFromUtc utcString={customer.birthDate} /> : null}
                            </>
                        }
                    />
                ) : null}
            </Stack>
            {routingRuleChangesDialog}
        </>
    );
};

type OwnPropsWithAppClockService = OwnProps & AppClockServiceProps;

export default pipe(
    CompanionListItem,
    connect<StateProps, DispatchProps, OwnPropsWithAppClockService, ApplicationState>(
        createStructuredSelector({
            customer: (state: ApplicationState, { customerId }) => getCustomer(state, { id: customerId }),
            accountNotes: (state: ApplicationState, { customerId }: OwnPropsWithAppClockService) =>
                getCustomerRelatedNotesString(state, { accountId: customerId, id: customerId }),
            hasValidExternalCreditCard: (
                state: ApplicationState,
                { customerId, appClockService }: OwnPropsWithAppClockService
            ) => hasCustomerValidExternalCreditCard(state, { accountId: customerId, appClockService }),
            reservationCustomerId: (state: ApplicationState, { reservationId }: OwnPropsWithAppClockService) =>
                getCurrentEnterpriseReservationCustomerId(state, { id: reservationId }),
        }),
        (
            dispatch: CommanderThunkDispatch,
            { refetchGroup, customerId: accountId, reservationId, companionshipId }
        ) => ({
            // @ts-expect-error $TSFixMeError: Argument of type 'ThunkAction<Action<any>, void, ApplicationState>' is not assignable to parameter of type 'Action<any>'...
            editInternals: () => dispatch(editInternalsAction({ accountId, onClose: refetchGroup })),
            openBillingScreen: () => dispatch(openBillingAccount({ accountId })),
            openPaymentsScreen: () => dispatch(openPayments({ accountId })),
            updateOwner: async () =>
                withActiveWindowLoader(() => dispatch(updateOwnerAction(reservationId, accountId))).then(() =>
                    refetchGroupAndShowSuccessFlash({ refetchGroup, messageKey: TextKey.SaveSuccessMessage })
                ),
            deleteCompanion: async () =>
                withActiveWindowLoader(() => dispatch(deleteCompanionship(companionshipId))).then(() =>
                    refetchGroupAndShowSuccessFlash({ refetchGroup, messageKey: TextKey.CompanionRemoved })
                ),
            unassignCompanion: async () =>
                withActiveWindowLoader(() => dispatch(unassignCompanionship(companionshipId))).then(() =>
                    refetchGroupAndShowSuccessFlash({ refetchGroup, messageKey: TextKey.Unassigned })
                ),
        })
    ),
    withAppClockService
);
